import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import Boton from '../components/Boton'
import { useHero } from '../hooks/useHero'

// Foto de stock (licencia Unsplash, uso comercial libre) como red de
// seguridad: se usa si todavía no se guardó nada desde el panel admin
// (/admin) o mientras esa lectura está en curso.
const IMAGEN_POR_DEFECTO = '/gatos/hero-bengal.webp'
const INTERVALO_ROTACION_MS = 7000

function Hero() {
  const sinMovimiento = useReducedMotion()
  const { imagenes } = useHero()
  const lista = useMemo(
    () => (imagenes.length > 0 ? imagenes : [IMAGEN_POR_DEFECTO]),
    [imagenes]
  )
  const [indice, setIndice] = useState(0)

  // La imagen del hero es el elemento LCP: Lighthouse mide su "render
  // delay" hasta que la animación de entrada termina, no solo hasta que
  // el archivo llega. Con fetchpriority=high + preload en index.html la
  // descarga ya no era el cuello de botella — el fade de 1.2s pasó a
  // serlo. Por eso la primerísima imagen aparece sin transición; el
  // crossfade normal se conserva para las rotaciones del carrusel
  // (montajes posteriores), que no cuentan para el LCP.
  const primerMontaje = useRef(true)
  useEffect(() => {
    primerMontaje.current = false
  }, [])

  // Si la lista cambia (llega la lectura real tras el fallback, o se
  // guardó una nueva desde el admin) se reinicia en la primera imagen en
  // vez de quedar apuntando a un índice que ya no existe.
  useEffect(() => setIndice(0), [lista])

  useEffect(() => {
    if (lista.length <= 1 || sinMovimiento) return
    const id = setInterval(() => setIndice((i) => (i + 1) % lista.length), INTERVALO_ROTACION_MS)
    return () => clearInterval(id)
  }, [lista, sinMovimiento])

  const aparecer = (retraso) =>
    sinMovimiento
      ? {}
      : {
          initial: { opacity: 0, y: 20 },
          animate: { opacity: 1, y: 0 },
          transition: {
            duration: 0.8,
            delay: retraso,
            ease: [0.22, 1, 0.36, 1],
          },
        }

  return (
    <section
      aria-labelledby="titulo-hero"
      // svh (no vh) para que la barra de direcciones de Safari/Chrome móvil no
      // deje el hero recortado al cargar. El mínimo cede en pantallas muy
      // bajas —móvil en horizontal— donde 85svh dejaría el texto sin aire.
      className="relative flex min-h-[85svh] items-center overflow-hidden"
    >
      {/*
        AnimatePresence en modo "sync" (por defecto) superpone la imagen que
        entra y la que sale mientras ambas están montadas — con las dos en
        position:absolute eso es justo el crossfade. Con reduced-motion no
        hay rotación (el efecto useEffect de arriba no arranca el timer), así
        que solo se ve la primera imagen sin animación.
      */}
      <div aria-hidden="true" className="absolute inset-0">
        <AnimatePresence>
          <motion.img
            key={lista[indice]}
            src={lista[indice]}
            alt=""
            fetchPriority="high"
            decoding="async"
            initial={sinMovimiento || primerMontaje.current ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={sinMovimiento ? undefined : { opacity: 0 }}
            transition={{ duration: 1.2, ease: 'easeInOut' }}
            className="absolute inset-0 size-full object-cover"
          />
        </AnimatePresence>
      </div>
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-linear-to-b from-noche/40 via-noche/70 to-noche"
      />
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-px bg-linear-to-r from-transparent via-oro-profundo to-transparent"
      />

      {/* El padding superior descuenta el encabezado fijo: sin esto el
          ojillo «Bengalí Leopard Cats» quedaba pisado por la barra en
          pantallas bajas. */}
      <div className="contenedor relative pt-[calc(var(--altura-encabezado)+3rem)] pb-16 sm:pb-24">
        <motion.p
          {...aparecer(0)}
          className="text-[0.7rem] uppercase tracking-[0.3em] text-oro sm:text-xs sm:tracking-[0.35em]"
        >
          Bengalí Leopard Cats
        </motion.p>

        <motion.h1
          {...aparecer(0.1)}
          id="titulo-hero"
          className="mt-5 max-w-3xl text-titular font-light sm:mt-6"
        >
          La belleza salvaje del gato Bengalí,
          <span className="text-oro-claro"> criados con excelencia</span>
        </motion.h1>

        <motion.p
          {...aparecer(0.2)}
          className="mt-6 max-w-xl text-entradilla text-marfil-suave/90 sm:mt-8"
        >
         Especialistas dedicados exclusivamente a la raza Bengalí.
          Garantizamos genética con rosetas marcadas, salud certificada y un temperamento equilibrado.
           Asesoría directa por WhatsApp.
        </motion.p>

        <motion.div
          {...aparecer(0.3)}
          className="mt-8 flex flex-wrap gap-4 sm:mt-10"
        >
          {/* A ancho de móvil el botón ocupa la línea completa: es la acción
              principal de la página y así gana área táctil. */}
          <Boton as="a" href="#catalogo" className="w-full xs:w-auto">
            Ver disponibles
          </Boton>
        </motion.div>
      </div>
    </section>
  )
}

export default Hero
